/**
 * perf-stats — minimal, allocation-free performance stats for Three.js / WebGL2 / WebGPU
 *
 * Usage:
 *   const stats = new PerfStats()
 *   await stats.init(renderer)
 *
 *   // render loop:
 *   stats.begin()
 *   renderer.render(scene, camera)
 *   stats.end()
 *   stats.update()
 *
 *   // read anytime:
 *   stats.data  // { fps: 60, cpu: 1.23, gpu: 0.87, gpuCompute: 0, frames: 1 }
 *
 *   // subscribe (throttled to notifyHz, default 10/sec):
 *   const unsub = stats.on(data => setStats(data))
 */

class RingBuffer {
  constructor(capacity) {
    this.buf = new Float64Array(capacity)
    this.head = 0
    this.count = 0
    this.sum = 0
  }

  push(value) {
    if (this.count === this.buf.length) {
      this.sum -= this.buf[this.head]
    } else {
      this.count++
    }
    this.buf[this.head] = value
    this.sum += value
    this.head = (this.head + 1) % this.buf.length
  }

  avg() {
    return this.count === 0 ? 0 : this.sum / this.count
  }

  reset() {
    this.head = 0
    this.count = 0
    this.sum = 0
  }
}

export class Stats {
  constructor({ samples = 60, notifyHz = 10 } = {}) {
    this.data = { fps: 0, cpu: 0, gpu: 0, gpuCompute: 0, frames: 0 }

    this.gl = null
    this.ext = null
    this.info = null

    this.listeners = []
    this.notifyInterval = 1000 / notifyHz
    this.lastNotifyTime = 0

    this.cpuStartTime = 0
    this.cpuRunning = false
    this.cpuTotal = 0

    this.activeQuery = null
    this.gpuQueries = []
    this.gpuTotal = 0
    this.gpuComputeTotal = 0

    this.frameCount = 0
    this.renderCount = 0
    this.prevFpsTime = performance.now()

    this.cpuAvg = new RingBuffer(samples)
    this.gpuAvg = new RingBuffer(samples)
    this.gpuComputeAvg = new RingBuffer(samples)
  }

  /** Subscribe to updates. Fires at notifyHz rate (default 10/sec). Returns unsubscribe fn. */
  on(fn) {
    this.listeners.push(fn)
    return () => {
      const i = this.listeners.indexOf(fn)
      if (i !== -1) this.listeners.splice(i, 1)
    }
  }

  async init(source) {
    if (source.isWebGPURenderer) {
      source.backend.trackTimestamp = true
      if (await source.hasFeatureAsync('timestamp-query')) {
        this.info = source.info
      }
      return
    }

    if (source.isWebGLRenderer) {
      this.gl = source.getContext()
    } else if (source instanceof WebGL2RenderingContext) {
      this.gl = source
    } else if (source instanceof HTMLCanvasElement || source instanceof OffscreenCanvas) {
      this.gl = source.getContext('webgl2')
    }

    if (this.gl) {
      this.ext = this.gl.getExtension('EXT_disjoint_timer_query_webgl2')
    }
  }

  begin() {
    if (!this.cpuRunning) {
      this.cpuStartTime = performance.now()
      this.cpuRunning = true
    }

    if (this.gl && this.ext) {
      if (this.activeQuery) {
        this.gl.endQuery(this.ext.TIME_ELAPSED_EXT)
      }
      this.activeQuery = this.gl.createQuery()
      if (this.activeQuery) {
        this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, this.activeQuery)
      }
    }
  }

  end() {
    this.renderCount++

    if (this.gl && this.ext && this.activeQuery) {
      this.gl.endQuery(this.ext.TIME_ELAPSED_EXT)
      this.gpuQueries.push(this.activeQuery)
      this.activeQuery = null
    }
  }

  update() {
    const now = performance.now()

    // GPU
    if (this.info) {
      this.gpuTotal = this.info.render.timestamp
      this.gpuComputeTotal = this.info.compute.timestamp
      this.gpuComputeAvg.push(this.gpuComputeTotal)
    } else {
      this._processGpuQueries()
    }

    // CPU
    if (this.cpuRunning) {
      this.cpuTotal += now - this.cpuStartTime
      this.cpuRunning = false
    }

    this.cpuAvg.push(this.cpuTotal)
    this.gpuAvg.push(this.gpuTotal)

    // FPS
    this.frameCount++
    const fpsElapsed = now - this.prevFpsTime
    if (fpsElapsed >= 1000) {
      this.data.fps = Math.round((this.frameCount * 1000) / fpsElapsed)
      this.frameCount = 0
      this.prevFpsTime = now
    }

    // write output
    this.data.cpu = this.cpuAvg.avg()
    this.data.gpu = this.gpuAvg.avg()
    this.data.gpuCompute = this.gpuComputeAvg.avg()
    this.data.frames = this.renderCount

    // notify listeners (throttled)
    if (this.listeners.length > 0 && now - this.lastNotifyTime >= this.notifyInterval) {
      this.lastNotifyTime = now
      for (let i = 0; i < this.listeners.length; i++) {
        this.listeners[i](this.data)
      }
    }

    // reset per-frame
    this.cpuTotal = 0
    this.gpuTotal = 0
    this.gpuComputeTotal = 0
    this.renderCount = 0
  }

  dispose() {
    if (this.gl) {
      for (let i = 0; i < this.gpuQueries.length; i++) {
        this.gl.deleteQuery(this.gpuQueries[i])
      }
      if (this.activeQuery) this.gl.deleteQuery(this.activeQuery)
    }
    this.gpuQueries.length = 0
    this.activeQuery = null
    this.listeners.length = 0
    this.cpuAvg.reset()
    this.gpuAvg.reset()
    this.gpuComputeAvg.reset()
  }

  _processGpuQueries() {
    if (!this.gl || !this.ext) return
    this.gpuTotal = 0

    let writeIdx = 0
    for (let i = 0; i < this.gpuQueries.length; i++) {
      const q = this.gpuQueries[i]
      const available = this.gl.getQueryParameter(q, this.gl.QUERY_RESULT_AVAILABLE)
      const disjoint = this.gl.getParameter(this.ext.GPU_DISJOINT_EXT)

      if (available && !disjoint) {
        const ns = this.gl.getQueryParameter(q, this.gl.QUERY_RESULT)
        this.gpuTotal += ns * 1e-6
        this.gl.deleteQuery(q)
      } else {
        this.gpuQueries[writeIdx++] = q
      }
    }
    this.gpuQueries.length = writeIdx
  }
}
